import { PropertyFiltersQuery } from "__generated__/api";
import _SortAndFilter = PropertyFiltersQuery._SortAndFilter;
import * as React from "react";
import { Filter, FilterOption } from "components/flexComponents/PropertyFilters/typings";
import { HotelSearchLinkBuilder } from "components/utility/HotelSearchLinkBuilder/HotelSearchLinkBuilder";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { PROPERTY_FILTERS_COMPONENT_MAP } from "components/shared/PropertyFIlterComponents/map/PropertyFiltersComponentMap";
import { Radio } from "components/shared/PropertyFIlterComponents/components/Radio";
import { PropertyFilterPublisher } from "./PropertyFilterPublisher";

/**
 * Class that describes the state of the filters. This will be used as the source of truth.
 *
 * filterName refers to name of the "category" (e.g. Property Class)
 * optionName refers to the individual options for a filter (e.g. 5 stars)
 */
export class PropertyFiltersContext {
  // e.g. ordered map of key = "propertyClass", value = map of 1 star, 2 star, etc
  private readonly filtersMap = new Map<string, Filter>();
  private readonly originalRegionId: string;
  private readonly filtersPublisher = new PropertyFilterPublisher();

  // Exclusively for Desktop since we can only select one filter at a time
  public setSelectedFilter: React.Dispatch<Filter>;

  public setNumberOfSelectedFilters: React.Dispatch<number>;

  constructor(filters: _SortAndFilter[], regionId: string) {
    filters
      .filter((filter) => filter?.options && filter?.label)
      .forEach((filter) => {
        const { name, label, options } = filter;

        const optionsMap = new Map<string, FilterOption>();
        options.forEach((option) => {
          optionsMap.set(option.optionValue || "", option);
        });

        this.filtersMap.set(name, { name, label: label || "", options: optionsMap });
      });

    this.originalRegionId = regionId;
  }

  public getListOfFilters() {
    return Array.from(this.filtersMap.values());
  }

  public isEmpty() {
    return this.getListOfFilters().length === 0;
  }

  public bindSetNumberOfSelectedFilters(setNumberOfSelectedFilters: React.Dispatch<number>) {
    this.setNumberOfSelectedFilters = setNumberOfSelectedFilters;
  }

  public getNumberOfSelectedFilters() {
    return this.getListOfFilters().reduce((count, filter) => {
      return count + this.getNumberOfSelectedFiltersPerCategory(filter.name);
    }, 0);
  }

  public getNumberOfSelectedFiltersPerCategory(filterName: string) {
    if (filterName === "popularFilter") {
      return 0;
    }

    const filters = this.filtersMap.get(filterName);
    const options = Array.from(filters?.options.values() || []);

    return options.filter((option) => option.isSelected && option.optionValue !== this.originalRegionId).length;
  }

  public addListener(filterName: string, optionName: string, setIsSelected: React.Dispatch<boolean>) {
    const filter = this.filtersMap.get(filterName);
    const option = filter?.options.get(optionName);

    if (!option) return;

    const setOption = (value: boolean) => {
      option.isSelected = value;
      setIsSelected(value);
    };

    this.filtersPublisher.subscribe(option.selectedLabel || "", setOption);
  }

  public isSelected(filterName: string, optionName: string) {
    return this.filtersMap.get(filterName)?.options.get(optionName)?.isSelected;
  }

  public toggleSelection = (filterName: string, optionName: string) => {
    if (!this.filtersMap.get(filterName)?.options.get(optionName)) {
      return;
    }

    const option = this.filtersMap.get(filterName)!.options.get(optionName)!;
    const optionIsSelected = option.isSelected;
    const optionKey = option.selectedLabel || "";
    const groupName = filterName === "popularFilter" ? option.filterCategory || "" : filterName;

    if (PROPERTY_FILTERS_COMPONENT_MAP[groupName] === Radio) {
      this.clearFilter(groupName);
    }

    this.filtersPublisher.next(optionKey, !optionIsSelected);

    if (this.setNumberOfSelectedFilters) {
      this.setNumberOfSelectedFilters(this.getNumberOfSelectedFilters());
    }
  };

  public clearAllFilters() {
    this.getListOfFilters().forEach((filter) => this.clearFilter(filter.name));
  }

  public clearFilter(filterName: string) {
    if (!this.filtersMap.get(filterName)?.options) {
      return;
    }

    this.filtersMap.get(filterName)!.options.forEach((option) => {
      this.filtersPublisher.next(option.selectedLabel || "", false);
    });
  }

  public buildLinkToSRP(context?: ExtendedContextStore) {
    const link = new HotelSearchLinkBuilder()
      .withStayLength()
      .withDaysInFuture()
      .withRegionId(this.originalRegionId)
      .withExpandForm();

    // Add filters
    this.getListOfFilters().forEach((filter) => {
      Array.from(filter.options.values())
        .filter((option) => option.isSelected)
        .forEach((option) => {
          // We dont want to get popular filters since they are propagated
          if (option.filterCategory) return;

          link.withFilter(filter.name, option.optionValue || "");
        });
    });

    return link.build(context);
  }
}

// Don't mind the defaults
export const FiltersContext = React.createContext(new PropertyFiltersContext([], "178288"));
